import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, RefreshCw, X, Filter, List, ChevronsLeft, ChevronsRight, Globe, Box, ArrowUpDown } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

// استيراد السياقات الموحدة
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { exportToExcel } from './exportUtils'; 

const FailureCodesList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // جلب معلومات الجلسة والصلاحيات
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';
  const { canCreate, isAdmin } = usePermissions('failure_codes');
  
  // سياق حجم الخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- States ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const failureClassFilter = searchParams.get('failure_class') || "";
  const descriptionFilter = searchParams.get('description') || "";
  const sortBy = searchParams.get('sortBy') || "failure_class";
  const sortDir = searchParams.get('sortDir') || "asc";
  const selectedOrg = searchParams.get('org_id') || "";
  const showAllSites = searchParams.get('all_sites') === 'true';

  // --- Dropdowns for Super Admin ---
  const [orgsList, setOrgsList] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const forceAll = isAdmin && (selectedOrg !== "");
      const effectiveAll = showAllSites || forceAll;

      const params = {
        page, limit, q, failure_class: failureClassFilter, description: descriptionFilter,
        org_id: sessionOrg,
        filtered_org: selectedOrg,
        all_sites: Boolean(effectiveAll), sortBy, sortDir
      };

      const res = await api.get('/failure-classes', { headers: { 'Authorization': `Bearer ${token}` }, params });
      setData(res.data.items || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load records"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, failureClassFilter, descriptionFilter, selectedOrg, showAllSites, sortBy, sortDir, isAdmin, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // جلب المنظمات للمدير العام
  useEffect(() => {
    if (isAdmin) {
      api.get('/organizations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => setOrgsList(res.data));
    }
  }, [isAdmin]);

  // --- Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleRowClick = (id) => {
    const queryString = new URLSearchParams(Object.fromEntries(searchParams.entries())).toString();
    navigate(`/failure-codes/${id}${queryString ? '?' + queryString : ''}`);
  };

  // --- Sidebar Events ---
  useEffect(() => {
      const openAllSites = () => updateFilters({ page: 1, all_sites: true });
      const viewMySite = () => updateFilters({ page: 1, all_sites: false });
      
      const exportData = () => {
          const exportParams = Object.fromEntries(searchParams.entries());
          const columnsMap = {
              "failure_class": t('failure_codes.fields.class', 'Failure Class'),
              "description": t('common.description', 'Description'),
              "org_id": t('common.org', 'Organization')
          };
          exportToExcel('failure-classes', exportParams, columnsMap, t);
      };

      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.addEventListener('GLOBAL_EXPORT', exportData);
      
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
          window.removeEventListener('GLOBAL_EXPORT', exportData);
      };
  }, [searchParams, t]);

  const totalPages = Math.ceil(totalItems / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] uppercase tracking-tighter font-bold ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* 1. Header & Global Search */}
      <div className="bg-white p-3 border-b shadow-sm z-20 space-y-3">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center gap-6">
            <h2 className="text-[#005495] font-black italic text-xl flex items-center gap-2">
              <RefreshCw size={22} className={`${loading ? 'animate-spin text-blue-500' : 'text-blue-900 cursor-pointer hover:rotate-180 transition-all duration-500'}`} onClick={() => fetchData()} title={t('common.refresh', 'Refresh')} />
              {t('failure_codes.list.title', 'Failure Codes Library')}
              <span className={`text-[0.9em] px-2 py-0.5 rounded-full border not-italic font-bold ml-2 ${showAllSites ? 'bg-red-50 text-red-600 border-red-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                  {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.org', 'ORG')}: ${sessionOrg}`}
              </span>
            </h2>
            <div className={`relative group ${isRTL ? 'mr-4' : 'ml-4'}`}>
              <Search className={`absolute top-2.5 text-gray-400 group-focus-within:text-blue-500 ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
              <input 
                className={`py-2 border border-gray-300 w-[400px] outline-none focus:border-blue-600 shadow-inner rounded-sm font-black text-blue-900 placeholder:font-normal placeholder:italic uppercase transition-all ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'}`} 
                placeholder={t('common.search')} 
                value={q}
                onChange={(e) => updateFilters({ q: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-red-500 font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            {/* Org Filter for SuperAdmin */}
            {isAdmin && (
              <div className="flex items-center gap-4 bg-gray-50 p-2 rounded-sm border border-gray-200 shadow-sm animate-in fade-in">
                <div className={`flex items-center gap-2 border-gray-300`}>
                  <Globe size={14} className="text-blue-600" />
                  <select className={`bg-transparent outline-none text-[0.9em] font-black uppercase cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`} value={selectedOrg} onChange={(e) => updateFilters({ filtered_org: e.target.value, page: 1 })}>
                    <option value="">{t('common.all_orgs', 'All Orgs')}</option>
                    {orgsList.map(o => <option key={o.org_id} value={o.org_id}>{o.org_id}</option>)}
                  </select>
                </div>
              </div>
            )}
            {canCreate && (
                <button onClick={() => navigate('/failure-codes/new')} className="bg-[#005495] text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 shadow-lg transition-all active:scale-95">
                <Plus size={16} /> {t('failure_codes.list.new', 'New Failure Class')}
                </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. Grid Content */}
      <div className="flex-1 p-4 overflow-auto custom-scrollbar">
        <div className="bg-white border shadow-sm rounded-sm overflow-hidden min-w-max min-h-[450px]">
          <table className="w-full text-left">
            <thead className="bg-[#f8f9fa] border-b text-gray-700 font-black uppercase text-[0.9em] sticky top-0 z-10 tracking-widest">
              <tr>
                <SortableHeader label={t('failure_codes.fields.class', 'Failure Class')} sortKey="failure_class" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} width="w-48" />
                <SortableHeader label={t('assets.headers.desc', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <th className={`p-3 border-gray-200 w-32 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.org', 'Organization')}</th>
              </tr>
              {/* Column Filters Row */}
              <tr className="bg-white border-b border-gray-200 shadow-sm">
                <td className="p-1 border-x border-gray-100">
                  <FilterInput value={failureClassFilter} onChange={v => updateFilters({ failure_class: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-gray-100">
                  <FilterInput value={descriptionFilter} onChange={v => updateFilters({ description: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-gray-100 bg-gray-50/50"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-bold">
              {loading ? (
                <tr><td colSpan="3" className="p-20 text-center italic text-blue-900 font-black animate-pulse tracking-[0.5em]">{t('common.syncing', 'Synchronizing Library...')}</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="3" className="p-20 text-center text-gray-400 italic font-black uppercase tracking-widest">{t('common.no_records', 'No Records Found')}</td></tr>
              ) : data.map((item) => (
                <tr 
                  key={item.id} 
                  onClick={() => handleRowClick(item.id)}
                  className={`hover:bg-blue-50/50 cursor-pointer group transition-all text-[0.9em] ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className={`p-3 border-gray-50 font-black text-[#005495] group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>
                     {item.failure_class}
                  </td>
                  <td className={`p-3 border-gray-50 text-gray-700 ${isRTL ? 'border-l' : 'border-r'}`}>{item.description || '---'}</td>
                  <td className="p-3 text-center text-gray-400">{item.org_id || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 3. Pagination Footer */}
      <div className={`bg-white border-t border-gray-200 p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[0.9em] text-gray-500 rounded-b-sm `}>
        <div className={`flex items-center gap-6 `}>
            <div className={`flex items-center gap-2 `}>
                <Box size={14} className="text-blue-500"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-blue-700">{(page-1)*limit+1}-{Math.min(page*limit, totalItems)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-gray-200 ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-orange-500"/>
                <span>{t('common.total', 'Total')}: <span className="text-orange-600">{totalItems}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 `}>
            <div className={`flex items-center gap-1 bg-gray-50 rounded px-2 py-1 border border-gray-200 `}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[0.9em] font-mono border-x border-gray-200 mx-1">
                    <span className="text-blue-600 text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-blue-600 disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL, width = "" }) => (
  <th className={`p-3 border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')} ${isRTL ? 'border-l' : 'border-r'} ${width}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : 'justify-between'} `}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-blue-500' : 'text-gray-300'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder = "", center = false, className = "" }) => (
  <input 
    className={`w-full p-1.5 text-blue-900 font-bold outline-none bg-transparent placeholder-gray-300 border border-transparent focus:border-blue-500 focus:bg-white rounded-sm transition-all ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} 
  />
);

export default FailureCodesList;